import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../Assets/images/brown-logo.svg";
import styles from "./login.module.css";
import PrimaryButton from "../../Components/Buttons/PrimaryButton";
import showPassIcon from "../../Assets/images/eye-on.svg";
import hidePassIcon from "../../Assets/images/eye-off.svg";
import { userLogin } from "../../APICalling/APICalling";
import logger from "../../Utils/logger";
import { useGlobalContext } from "../../Context/GlobalContext";
import ReCAPTCHA from "react-google-recaptcha";
import { setTokens, initializeTokenManager, getToken } from "../../Utils/tokenManager";

const Login = () => {
	const { setLoggedOutletID, setLoggedUser, setLoggedName,setLoggedEmail, setUserAssignedOutlets } =
		useGlobalContext();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [recaptchaToken, setRecaptchaToken] = useState("");
	const [recaptchaError, setRecaptchaError] = useState("");
	const recaptchaRef = useRef(null);
	const navigate = useNavigate();
	const recaptchaSiteKey = process.env.REACT_APP_SITE_KEY;
	const hasRecaptchaSiteKey =
		typeof recaptchaSiteKey === "string" && recaptchaSiteKey.trim().length > 0;

	useEffect(() => {
		const existingToken = getToken();
		if (existingToken) {
			navigate("/");
		}
	}, [navigate]);

	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleLogin = async (e) => {
		e.preventDefault();
		setError("");
		setEmailError("");
		setPasswordError("");
		setRecaptchaError("");

		if (!email) {
			setEmailError("Please enter your email address");
			return;
		}

		if (!validateEmail(email)) {
			setEmailError("Invalid email format.");
			return;
		}

		if (!password) {
			setPasswordError("Please enter your password");
			return;
		}

		if (!hasRecaptchaSiteKey) {
			setError("reCAPTCHA is not configured. Please contact admin.");
			return;
		}

		if (!recaptchaToken) {
			setRecaptchaError("Please complete the reCAPTCHA.");
			return;
		}

		try {
			const loginData = { email, password, recaptcha_token: recaptchaToken };
			logger.log('🔐 Login attempt:', email);
			const response = await userLogin(loginData);

			if (response.data.success) {
				const fullTokenData = response.data.data.token; // Entire token object with expires_in
				const user = response.data.data.user;
				// ✅ Check for role
				const hasValidRole = user.role?.includes("Receptionist");

				if (!hasValidRole) {
					setError("You are not authenticated to login.");
					return;
				}

				const outletId = user.outlet_id;
				const userId = user.id;
				const userName = user.name;
				const userEmail = user.email;

				setLoggedOutletID(outletId);
				setLoggedUser(user?.id);
				setLoggedName(userName);
				setLoggedEmail(userEmail);
				setUserAssignedOutlets(user.outlet_details);

				// ✅ Use tokenManager to store tokens with expiry tracking and auto-refresh scheduling
				setTokens(fullTokenData);

				localStorage.setItem("outlet_id", String(outletId));
				localStorage.setItem("user_id", String(userId));
				localStorage.setItem("user_name", JSON.stringify(userName));
				localStorage.setItem("user_assigned_outlets",JSON.stringify(user.outlet_details));
				localStorage.setItem("user_email", String(userEmail));

				// Initialize token refresh scheduler
				initializeTokenManager();

				navigate("/");
			} else {
				setError(response.message || "Login failed. Please try again.");
				recaptchaRef.current?.reset?.();
				setRecaptchaToken("");
			}
		} catch (error) {
			const errorMessage =
				error.response?.message || "Invalid Credentials";
			setError(errorMessage);
			console.error("Login failed", error.response?.message);
			recaptchaRef.current?.reset?.();
			setRecaptchaToken("");
		}
	};

	return (
		<div className={styles.loginWrapper}>
			<div className={styles.loginCard}>
				<Link to="/" className={styles.formLogo}>
					<img src={Logo} alt="Natureland" />
				</Link>
				<form onSubmit={handleLogin}>
					<div className={styles.formHeader}>
						<h1>Login</h1>
						<p>Enter your email and password to login</p>
					</div>
					{error && (
						<p
							className={`${styles.errorMessage} ${styles.formError}`}>
							{error}
						</p>
					)}
					<div className="formElements">
						<div className="formGroup">
							<label className="formLabel">Email</label>
							<input
								className={`${styles.formControl} form-control`}
								type="text"
								placeholder="Enter Email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
							{emailError && (
								<p className={styles.errorMessage}>
									{emailError}
								</p>
							)}
						</div>
						<div className="formGroup">
							<label className="formLabel">Password</label>
							<div className={styles.inputGroup}>
								<input
									className={`${styles.formControl} form-control`}
									type={showPassword ? "text" : "password"}
									placeholder="Enter Password"
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
								/>
								<span
									className={styles.showIcon}
									onClick={() =>
										setShowPassword(!showPassword)
									}>
									<img
										src={
											showPassword
												? hidePassIcon
												: showPassIcon
										}
										alt="Toggle visibility"
									/>
								</span>
							</div>
							{passwordError && (
								<div className={styles.errorMessage}>
									{passwordError
										.split("\n")
										.map((msg, index) => (
											<p key={index}>{msg}</p>
										))}
								</div>
							)}
						</div>
						{hasRecaptchaSiteKey ? (
							<>
								<div className={styles.recaptchaContainer}>
									<ReCAPTCHA
										ref={recaptchaRef}
										sitekey={recaptchaSiteKey.trim()}
										onChange={(token) => {
											setRecaptchaToken(token || "");
											setRecaptchaError("");
										}}
										onExpired={() => {
											setRecaptchaToken("");
											setRecaptchaError(
												"reCAPTCHA expired. Please try again."
											);
										}}
										onErrored={() => {
											setRecaptchaToken("");
											setRecaptchaError(
												"reCAPTCHA failed to load. Please refresh and try again."
											);
										}}
									/>
								</div>
								{recaptchaError && (
									<p className={styles.errorMessage}>
										{recaptchaError}
									</p>
								)}
							</>
						) : (
							<p className={styles.errorMessage}>
								reCAPTCHA site key missing. Ensure{" "}
								<code>REACT_APP_SITE_KEY</code> is set and restart the
								dev server.
							</p>
						)}
						<PrimaryButton
							text="Login"
							type="submit"
							className={styles.AuthButton}
						/>
						{/* <div className={styles.bottomLink}>
							<Link
								to="/forgotpassword"
								className={styles.linkText}>
								Forgot your password?
							</Link>
						</div> */}
					</div>
				</form>
			</div>
		</div>
	);
};

export default Login;
